import React from 'react';
import { Send } from 'lucide-react';
import TemplateCard from './TemplateCard';
import { templates } from './templates';

function ChatArea({
  activeThreadId,
  threadMessages,
  isLoading,
  error,
  query,
  setQuery,
  handleTemplateClick,
  handleQuerySubmit,
  setSelectedCitation,
}) {
  const getBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'expired':
        return 'bg-danger text-white';
      case 'active':
        return 'bg-success text-white';
      case 'pending':
        return 'bg-info text-white';
      default:
        return 'bg-secondary text-white';
    }
  };

  // Show loading state during initialization
  if (!activeThreadId && isLoading) {
    return (
      <div className="flex-grow-1 d-flex flex-column">
        <div className="flex-grow-1 p-4 overflow-auto text-center d-flex align-items-center justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if initialization fails
  if (error) {
    return (
      <div className="flex-grow-1 d-flex flex-column">
        <div className="flex-grow-1 p-4 overflow-auto text-center d-flex align-items-center justify-content-center">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-grow-1 d-flex flex-column bg-light">
      <div className="flex-grow-1 p-4 overflow-auto">
        {threadMessages[activeThreadId] &&
        threadMessages[activeThreadId].length === 0 ? (
          <div className="text-center p-4">
            <h2 className="h4 mb-2 text-dark">Welcome to ContractGenie™</h2>
            <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
              Get started with these popular queries or ask your own question.
            </p>
            <div className="row row-cols-1 row-cols-md-2 g-3">
              {templates.map((template, index) => (
                <TemplateCard
                  key={index}
                  template={template}
                  onClick={() => handleTemplateClick(template)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="container">
            {(threadMessages[activeThreadId] || []).map((message) => (
              <div
                key={message.id}
                className={`d-flex ${
                  message.isUser
                    ? 'justify-content-end'
                    : 'justify-content-start'
                } mb-4`}
              >
                <div
                  className={`rounded p-3 shadow-sm ${
                    message.isUser
                      ? 'bg-primary text-white'
                      : 'bg-white text-dark'
                  }`}
                  style={{ maxWidth: '85%', width: '100%' }}
                >
                  {message.isUser && (
                    <div
                      className="mb-2"
                      style={{ fontSize: '1rem', lineHeight: '1.5' }}
                    >
                      {message.content}
                    </div>
                  )}

                  {!message.isUser && (
                    <div
                      className="mb-2"
                      style={{ fontSize: '1rem', lineHeight: '1.5' }}
                    >
                      We just found matches for your query in{' '}
                      {message.total_count} contracts.
                    </div>
                  )}

                  {message.table && (
                    <div
                      className="table-responsive"
                      style={{ overflowX: 'auto', maxWidth: '100%' }}
                    >
                      <table
                        className="table table-bordered"
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                        }}
                      >
                        <thead>
                          <tr>
                            {message.table.columns.map((column, index) => (
                              <th
                                key={index}
                                className="fw-medium text-capitalize"
                                style={{
                                  backgroundColor: message.isUser
                                    ? '#ffffff20'
                                    : '#f8f9fa',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                  minWidth:
                                    index === message.table.columns.length - 1
                                      ? '250px'
                                      : '150px',
                                  maxWidth:
                                    index === message.table.columns.length - 1
                                      ? '400px'
                                      : 'auto',
                                }}
                              >
                                {column}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {message.table.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {row.map((cell, colIndex) => (
                                <td
                                  key={colIndex}
                                  style={{
                                    verticalAlign: 'top',
                                    padding: '12px',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'normal',
                                    minWidth:
                                      colIndex === row.length - 1
                                        ? '250px'
                                        : '150px',
                                    maxWidth:
                                      colIndex === row.length - 1
                                        ? '400px'
                                        : 'auto',
                                    overflowX: 'scroll',
                                    scrollbarWidth: 'none',
                                    '-ms-overflow-style': 'none',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: '13px',
                                      lineHeight: '1.5',
                                      margin: 0,
                                      fontWeight: 400,
                                      wordWrap: 'break-word',
                                    }}
                                  >
                                    {cell}
                                  </p>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {message.additional_info && (
                    <div className="mt-3">
                      <div className="text-muted">
                        {message.additional_info}
                      </div>
                    </div>
                  )}

                  {message.citations && (
                    <div className="mt-3 pt-3 border-top">
                      <div className="small fw-medium mb-2 text-muted">
                        Sources:
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {message.citations.map((cit, i) => {
                          const isString = typeof cit === 'string';
                          const citationText = isString
                            ? cit
                            : `${cit.document}${
                                cit.page ? ` (p. ${cit.page})` : ''
                              }`;

                          return (
                            <button
                              key={i}
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                setSelectedCitation(
                                  isString ? { document: cit } : cit
                                )
                              }
                              title={citationText}
                              style={{
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <span className="me-1 fw-medium">[{i + 1}]</span>
                              <span>{citationText}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div
                    className={`small mt-2 ${
                      message.isUser ? 'text-white-50' : 'text-muted'
                    }`}
                    style={{ fontSize: '0.8rem' }}
                  >
                    {message.timestamp}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="d-flex justify-content-start mb-4">
                <div className="rounded p-3 bg-white shadow-sm d-flex align-items-center">
                  <div
                    className="spinner-border spinner-border-sm text-primary me-2"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                    Processing your request...
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className="p-3 border-top bg-white shadow-sm"
        style={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
        }}
      >
        <div className="input-group">
          <input
            type="text"
            className="form-control rounded-start-pill"
            placeholder="Ask me anything about your contracts..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleQuerySubmit()}
            style={{
              border: '1px solid #ced4da',
              fontSize: '0.95rem',
              // Ensure placeholder is visible
              color: '#495057', // Text color
              backgroundColor: '#fff', // Background color
            }}
          />
          <style jsx>{`
            input::placeholder {
              color: #6c757d !important; /* Bootstrap's text-muted color */
              opacity: 1 !important; /* Ensure full visibility */
            }
          `}</style>
          <button
            className="btn btn-primary rounded-end-pill"
            onClick={handleQuerySubmit}
            style={{ padding: '0.5rem 1rem' }}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatArea;
